import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as LegalIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CookieConsentBanner from '../components/CookieConsentBanner';
import { useLanguage } from '../contexts/LanguageContext';
import { PUBLIC_LANGUAGES, tPublic } from '../i18n/publicSite';

const NAV_LINKS = [
  { id: 'home', labelKey: 'navHome', href: 'https://knls.in' },
  { id: 'services', labelKey: 'navServices', to: '/services' },
  { id: 'courses', labelKey: 'navCourses', to: '/training-programs' },
  { id: 'jobAlerts', labelKey: 'navJobAlerts', to: '/job-alerts' },
  { id: 'enquiry', labelKey: 'navEnquiry', to: '/enquiryform' },
];

const LEGAL_LINKS = [
  { id: 'privacy', labelKey: 'privacyPolicy', to: '/privacy-policy' },
  { id: 'locsyTerms', labelKey: 'locsyTerms', to: '/locsy-terms-and-conditions' },
  { id: 'lmsTerms', labelKey: 'lmsTerms', to: '/terms-and-conditions' },
  { id: 'disclaimer', labelKey: 'disclaimer', to: '/disclaimer' },
  { id: 'refundPolicy', labelKey: 'refundPolicy', to: '/refund-policy' },
  { id: 'cookiePolicy', labelKey: 'cookiePolicy', to: '/cookie-policy' },
  { id: 'deleteAccount', labelKey: 'deleteAccount', to: '/delete-account' },
];

const FOOTER_LINKS = [
  { id: 'privacy', labelKey: 'privacyPolicy', to: '/privacy-policy' },
  { id: 'locsyTerms', labelKey: 'locsyTerms', to: '/locsy-terms-and-conditions' },
  { id: 'disclaimer', labelKey: 'disclaimer', to: '/disclaimer' },
  { id: 'refundPolicy', labelKey: 'refundPolicy', to: '/refund-policy' },
  { id: 'cookiePolicy', labelKey: 'cookiePolicy', to: '/cookie-policy' },
  { id: 'deleteAccount', labelKey: 'deleteAccount', to: '/delete-account' },
  { id: 'services', labelKey: 'navServices', to: '/services' },
  { id: 'jobAlerts', labelKey: 'navJobAlerts', to: '/job-alerts' },
  { id: 'enquiry', labelKey: 'navEnquiry', to: '/enquiryform' },
];

/**
 * Shared public chrome (nav + footer) for marketing / legal pages on services.knls.in.
 */
export default function PublicSiteLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();
  const t = (key, vars) => tPublic(language, key, vars);
  const [legalAnchor, setLegalAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.documentElement.lang = language || 'en';
  }, [language]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const closeMobile = () => setMobileOpen(false);

  const navButtonSx = (active) => ({
    color: 'white',
    textTransform: 'none',
    fontWeight: active ? 700 : 500,
    opacity: active ? 1 : 0.9,
    borderBottom: active ? '2px solid #93C5FD' : '2px solid transparent',
    borderRadius: 0,
    px: 1.5,
    minWidth: 0,
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
  });

  return (
    <Box
      lang={language}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f7fb',
        scrollBehavior: 'smooth',
        fontFamily:
          language === 'te'
            ? '"Noto Sans Telugu", "Nirmala UI", "Gautami", sans-serif'
            : language === 'hi'
              ? '"Noto Sans Devanagari", "Nirmala UI", "Mangal", sans-serif'
              : undefined,
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        component="header"
        sx={{
          background: 'linear-gradient(90deg, #0F172A 0%, #1E3A8A 45%, #2563EB 100%)',
          boxShadow: '0 8px 24px rgba(15,23,42,0.2)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 }, gap: 1 }}>
            <Link
              href="https://knls.in"
              underline="none"
              color="inherit"
              aria-label={t('brandHome')}
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 800,
                  fontSize: 14,
                }}
              >
                  L
                </Box>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1}>
                  Loksy
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t('locsyCitizen')}
                </Typography>
              </Box>
            </Link>

            <Box sx={{ flexGrow: 1 }} />

            {!isMobile && (
              <Box component="nav" aria-label="Primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {NAV_LINKS.map((item) =>
                  item.href ? (
                    <Button
                      key={item.id}
                      href={item.href}
                      sx={navButtonSx(false)}
                      rel="noopener noreferrer"
                    >
                      {t(item.labelKey)}
                    </Button>
                  ) : (
                    <Button
                      key={item.to}
                      component={RouterLink}
                      to={item.to}
                      sx={navButtonSx(isActive(item.to))}
                    >
                      {t(item.labelKey)}
                    </Button>
                  )
                )}
                <Button
                  endIcon={<ExpandMoreIcon />}
                  startIcon={<LegalIcon sx={{ fontSize: 18 }} />}
                  onClick={(e) => setLegalAnchor(e.currentTarget)}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(legalAnchor)}
                  aria-controls={legalAnchor ? 'legal-menu' : undefined}
                  sx={navButtonSx(LEGAL_LINKS.some((l) => isActive(l.to)))}
                >
                  {t('navLegal')}
                </Button>
                <Menu
                  id="legal-menu"
                  anchorEl={legalAnchor}
                  open={Boolean(legalAnchor)}
                  onClose={() => setLegalAnchor(null)}
                  MenuListProps={{ 'aria-label': 'Legal pages' }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {LEGAL_LINKS.map((item) => (
                    <MenuItem
                      key={item.to}
                      component={RouterLink}
                      to={item.to}
                      selected={isActive(item.to)}
                      onClick={() => setLegalAnchor(null)}
                    >
                      {t(item.labelKey)}
                    </MenuItem>
                  ))}
                </Menu>
                <LanguageSwitcher />
              </Box>
            )}

            {isMobile && (
              <>
                <LanguageSwitcher />
                <IconButton
                  color="inherit"
                  edge="end"
                  aria-label={t('navOpenMenu')}
                  onClick={() => setMobileOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeMobile}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography fontWeight={700}>{t('navMenu')}</Typography>
          <IconButton aria-label={t('navCloseMenu')} onClick={closeMobile}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {PUBLIC_LANGUAGES.map((lang) => (
            <Button
              key={lang.code}
              size="small"
              variant={language === lang.code ? 'contained' : 'outlined'}
              onClick={() => changeLanguage(lang.code)}
              sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}
            >
              {lang.nativeName}
            </Button>
          ))}
        </Box>
        <Divider />
        <List component="nav" aria-label="Mobile primary">
          {NAV_LINKS.map((item) =>
            item.href ? (
              <ListItemButton
                key={item.id}
                component="a"
                href={item.href}
                onClick={closeMobile}
                rel="noopener noreferrer"
              >
                <ListItemText primary={t(item.labelKey)} />
              </ListItemButton>
            ) : (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                selected={isActive(item.to)}
                onClick={closeMobile}
              >
                <ListItemText primary={t(item.labelKey)} />
              </ListItemButton>
            )
          )}
        </List>
        <Divider />
        <Typography variant="overline" sx={{ px: 2, pt: 2, color: 'text.secondary' }}>
          {t('navLegal')}
        </Typography>
        <List component="nav" aria-label={t('navLegal')}>
          {LEGAL_LINKS.map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={isActive(item.to)}
              onClick={closeMobile}
            >
              <ListItemText primary={t(item.labelKey)} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flex: 1 }}>
        {children}
      </Box>

      <Box
        component="footer"
        sx={{
          mt: 'auto',
          background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
          color: 'rgba(255,255,255,0.88)',
          pt: 5,
          pb: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr 1fr' },
              gap: 4,
              mb: 4,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                Loksy
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75, maxWidth: 320 }}>
                {t('footerBlurb')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ letterSpacing: 0.4 }}>
                {t('navLegal')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {FOOTER_LINKS.slice(0, 6).map((item) => (
                  <Link
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    color="inherit"
                    underline="hover"
                    sx={{ opacity: 0.8, width: 'fit-content' }}
                  >
                    {t(item.labelKey)}
                  </Link>
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ letterSpacing: 0.4 }}>
                {t('contact')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                <Link href="https://loksy.in" color="inherit" underline="hover" rel="noopener noreferrer" target="_blank">
                  https://loksy.in
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                <Link href="mailto:support@knls.in" color="inherit" underline="hover">
                  support@knls.in
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                <Link href="tel:+919885750476" color="inherit" underline="hover">
                  9885750476
                </Link>
                {' · '}
                <Link href="tel:+917815970729" color="inherit" underline="hover">
                  7815970729
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                <Link
                  component={RouterLink}
                  to="/enquiryform"
                  color="inherit"
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {t('enquiryForm')}
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                <Link component={RouterLink} to="/job-alerts" color="inherit" underline="hover">
                  {t('navJobAlerts')}
                </Link>
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 2 }} />
          <Typography variant="caption" sx={{ opacity: 0.65 }}>
            {t('copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Container>
      </Box>
      <CookieConsentBanner />
    </Box>
  );
}
